/**
 * The three views over one workflow read model.
 *
 * `NextActionPanel`, `HealthChecklist` and `AttentionList` all render the same
 * `ProjectWorkflow` object the backend evaluated. None of them fetches, none
 * of them derives a state, and none of them decides what a project needs -
 * they are presentation over an answer that was already computed once, which
 * is what stops the project screen and the library from disagreeing.
 *
 * Accessibility rules that hold across all three:
 *
 * - **State is never colour alone.** Every checklist row carries a glyph and
 *   a screen-reader label as well as a tone class.
 * - **`todo` is not a warning.** It gets the neutral treatment, so a young
 *   project does not open covered in amber.
 * - **Informational next actions have no button.** When there is nothing to
 *   press, the panel is a sentence; a disabled primary button would imply an
 *   action that exists.
 */

import { Link } from "react-router-dom";
import type { ProjectWorkflow, SubjectType, WorkflowActionKey } from "@/api/types";
import { paths } from "@/app/paths";
import {
  ACTION_BUTTONS,
  ACTION_TITLES,
  attentionText,
  ATTENTION_DESTINATION,
  STEP_STATE_LABEL,
  STEP_STATE_MARK,
  STEP_STATE_TONE,
  stepDetail,
  stepTitle,
} from "./vocabulary";
import "./workflow.css";

/** Where each action leads. One map, so no screen invents its own route. */
function actionHref(key: WorkflowActionKey, projectId: string): string {
  switch (key) {
    case "add_capture":
    case "phone_capture":
    case "upload_media":
    case "run_analysis":
    case "review_quality":
      return paths.projectSessions(projectId);
    case "prepare_videos":
    case "start_reconstruction":
    case "view_reconstruction":
      return paths.projectReconstruction(projectId);
    case "measure_coverage":
      return paths.projectCoverage(projectId);
    case "add_correction_capture":
      return paths.newCorrectionSession(projectId);
    case "view_outputs":
      return paths.projectFiles(projectId);
  }
}

export function NextActionPanel({
  workflow,
  projectId,
  compact = false,
}: {
  workflow: ProjectWorkflow;
  projectId: string;
  /** The card variant: headline plus one link, no secondary row. */
  compact?: boolean;
}) {
  const action = workflow.next_action;
  if (!action) return null;

  const title = ACTION_TITLES[action.key];

  if (compact) {
    return (
      <p className="next-action next-action--compact">
        <span className="next-action__title">{title}</span>
      </p>
    );
  }

  return (
    <section
      className={`next-action${action.informational ? " next-action--informational" : ""}`}
      aria-labelledby="next-action-heading"
    >
      <p className="next-action__eyebrow">Sonraki adım</p>
      <h2 id="next-action-heading" className="next-action__title">
        {title}
      </h2>
      <div className="next-action__buttons">
        {/*
          An informational action still links somewhere useful, but as a
          secondary control: a finished stage is a statement, and dressing it
          as a primary button would make finishing look like a task.
        */}
        <Link
          className={`btn ${action.informational ? "btn--secondary" : "btn--primary"}`}
          to={actionHref(action.key, projectId)}
        >
          {ACTION_BUTTONS[action.key]}
        </Link>
        {action.secondary.map((key) => (
          <Link key={key} className="btn btn--secondary" to={actionHref(key, projectId)}>
            {ACTION_BUTTONS[key]}
          </Link>
        ))}
      </div>
    </section>
  );
}

export function HealthChecklist({
  workflow,
  subjectType,
}: {
  workflow: ProjectWorkflow;
  subjectType: SubjectType;
}) {
  return (
    <section className="checklist" aria-labelledby="checklist-heading">
      <h2 id="checklist-heading" className="checklist__heading">
        Proje durumu
      </h2>
      {/*
        A list, not a progress bar. There is no percentage anywhere in this
        component and there must not be one: the steps are not equally sized,
        not all applicable to every subject, and "%62 hazır" would be a number
        with no unit behind it.
      */}
      <ul className="checklist__list">
        {workflow.steps.map((step) => (
          <li
            key={step.key}
            className={`checklist__item checklist__item--${STEP_STATE_TONE[step.state]}`}
          >
            <span className="checklist__mark" aria-hidden="true">
              {STEP_STATE_MARK[step.state]}
            </span>
            <span className="checklist__body">
              <span className="checklist__title">
                {stepTitle(step.key)}
                {/* The state in words, for anyone who cannot see the glyph or
                    the colour. Visually hidden, never absent. */}
                <span className="visually-hidden"> — {STEP_STATE_LABEL[step.state]}</span>
              </span>
              <span className="checklist__detail">{stepDetail(step, subjectType)}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function AttentionList({
  items,
  projectId,
}: {
  items: ProjectWorkflow["attention"];
  projectId: string;
}) {
  // Rendered by the caller only when non-empty; guarded here as well so a
  // future caller cannot produce an empty warning box.
  if (items.length === 0) return null;

  return (
    <ul className="attention__list">
      {items.map((item) => (
        <li key={item.key} className="attention__item">
          <span className="attention__text">{attentionText(item.key, item.count)}</span>
          <Link
            className="attention__link"
            to={
              ATTENTION_DESTINATION[item.key] === "reconstruction"
                ? paths.projectReconstruction(projectId)
                : ATTENTION_DESTINATION[item.key] === "coverage"
                  ? paths.projectCoverage(projectId)
                  : paths.projectSessions(projectId)
            }
          >
            Görüntüle
          </Link>
        </li>
      ))}
    </ul>
  );
}
